import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Star, MapPin, ArrowRight } from "lucide-react";

const Providers = () => {
  const [params, setParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [city, setCity] = useState(params.get("city") || "");
  const [zip, setZip] = useState(params.get("zip") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchP = async () => {
      setLoading(true);
      const q = new URLSearchParams();
      if (city) q.set("city", city);
      if (zip) q.set("zip", zip);
      try {
        const { data } = await api.get(`/providers?${q.toString()}`);
        if (!cancelled) setProviders(data);
      } catch (e) {
        console.error("Failed to load providers:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchP();
    return () => { cancelled = true; };
  }, [city, zip]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-12">
        <div className="tiny-label mb-2">Vyhledat</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Prádelny v okolí</h1>
        <p className="mt-2 text-slate-600">Filtrujte podle města nebo PSČ.</p>

        <Card className="p-5 mt-6 flex flex-wrap gap-3">
          <Input placeholder="Město (např. Praha)" value={city} onChange={(e) => setCity(e.target.value)} className="max-w-xs" data-testid="prov-search-city" />
          <Input placeholder="PSČ" value={zip} onChange={(e) => setZip(e.target.value)} className="max-w-[140px]" data-testid="prov-search-zip" />
        </Card>

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <div className="col-span-full text-center text-slate-500 py-10">Načítání…</div>
          ) : providers.length === 0 ? (
            <div className="col-span-full text-center text-slate-500 py-10">Žádné prádelny v okolí. Zkuste jiné město.</div>
          ) : (
            providers.map((p) => (
              <Card key={p.id} className="p-6 hover:shadow-md transition-all duration-300" data-testid={`provider-card-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 grid place-items-center text-white font-display font-bold text-lg">
                    {p.business_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-display font-bold text-slate-900">{p.business_name}</h3>
                      {p.verified && <span title="Ověřená prádelna" className="text-emerald-600 text-sm">✓</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-slate-600 line-clamp-3">{p.description || "Profesionální prádelna ve vašem okolí."}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-sm">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="font-semibold">{p.rating_avg || "—"}</span>
                    <span className="text-slate-500">({p.rating_count} hodnocení)</span>
                  </div>
                  <Link to={`/pradelna/${p.id}`} className="text-sm font-semibold text-sky-600 inline-flex items-center gap-1 hover:underline" data-testid={`provider-link-${p.id}`}>
                    Profil <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Providers;
